import React, { useState, useCallback } from 'react';
import HeroSection from '../components/HeroSection';
import Lightbox from '../components/Lightbox';
import Picture from '../components/Picture';
import SEO from '../components/SEO';

const HERO_IMG = '/images/glossfest-hero.jpg';

const fridayImages = [
  '/images/glossfest-fri-1.jpg',
  '/images/glossfest-fri-2.jpg',
  '/images/glossfest-fri-3.jpg',
  '/images/glossfest-fri-4.jpg',
];

const saturdayImages = [
  '/images/glossfest-sat-1.jpg',
  '/images/glossfest-sat-2.jpg',
  '/images/glossfest-sat-3.jpg',
  '/images/glossfest-sat-4.jpg',
  '/images/glossfest-sat-5.jpg',
  '/images/glossfest-sat-6.jpg',
];

const sundayImages = [
  '/images/glossfest-sun-1.jpg',
  '/images/glossfest-sun-2.jpg',
  '/images/glossfest-sun-3.jpg',
  '/images/glossfest-sun-4.jpg',
  '/images/glossfest-sun-5.jpg',
  '/images/glossfest-sun-6.jpg',
];

const allImages = [...fridayImages, ...saturdayImages, ...sundayImages];

function ImageGallery({ images, title, onImageClick, indexOffset }) {
  return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-[#0A0A0A]">{title}</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {images.map((img, i) => (
          <button
            key={img}
            data-testid={`glossfest-gallery-image-${indexOffset + i}`}
            onClick={() => onImageClick(indexOffset + i)}
            className="overflow-hidden cursor-pointer group focus:outline-none focus:ring-2 focus:ring-[#F0A500]"
          >
            <Picture
              src={img}
              alt={`${title} ${i + 1}`}
              className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function GlossFestPage() {
  const totalImages = allImages.length;
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = useCallback((index) => setLightboxIndex(index), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevImage = useCallback(() => {
    setLightboxIndex((i) => (i <= 0 ? totalImages - 1 : i - 1));
  }, [totalImages]);
  const nextImage = useCallback(() => {
    setLightboxIndex((i) => (i >= totalImages - 1 ? 0 : i + 1));
  }, [totalImages]);

  return (
    <div data-testid="glossfest-page">
      <SEO
        title="GlossFest — Music in the Heart of Glossop"
        description="GlossFest — bi-annual music festival in Norfolk Square, Glossop. Local bands, food vendors, craft beer festival and community celebrations. Next event 2027."
        path="/glossfest"
        keywords={['GlossFest', 'GlossFest Glossop', 'Glossop music festival', 'Glossop beer festival', 'Norfolk Square festival', 'live music Glossop', 'Glossop Creative Trust']}
      />
      {lightboxIndex !== null && (
        <Lightbox
          images={allImages}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}

      <HeroSection
        image={HERO_IMG}
        title="GlossFest"
        subtitle="Music in the Heart of Glossop"
        fullHeight={false}
      />

      {/* Content */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4 text-base text-[#52525B] leading-relaxed mb-16">
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-4">Bi-Annual Event</span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A]">
              GlossFest – Music in the Heart of Glossop
            </h2>
            <p>
              In October 2024 we met with Glossop Creative Trust to form a committee for a music festival in the heart of Glossop, Norfolk Square. Along with local bands performing on stage, we wanted to incorporate into the festivities local businesses, local food vendors, local pubs and bars along with our legendary Charity Beer Festival which this year included Cocktails by Simon and Jenny.
            </p>
            <p>
              The turnout was excellent and the weekend was exceptionally successful. A massive thank you to everyone who came down.
            </p>
            <p>
              Due to the overwhelming preparation, this is a bi-annual event running in 2025, 2027 and so on. Further details at{' '}
              <a href="https://www.glossfest.com/" target="_blank" rel="noopener noreferrer" className="text-[#F0A500] hover:text-[#D99500] font-semibold transition-colors">GlossFest</a>.
            </p>
            <p>
              If you would like to get involved please email{' '}
              <a href="mailto:volunteers@glossoproundtable.org" className="text-[#F0A500] hover:text-[#D99500] font-semibold transition-colors">volunteers@glossoproundtable.org</a>{' '}
              or find us on{' '}
              <a href="https://www.facebook.com/glossoprt/" target="_blank" rel="noopener noreferrer" className="text-[#F0A500] hover:text-[#D99500] font-semibold transition-colors">Facebook</a>.
            </p>
          </div>

          {/* Photo Galleries */}
          <div className="space-y-12">
            <ImageGallery images={fridayImages} title="GlossFest Friday 23rd May" onImageClick={openLightbox} indexOffset={0} />
            <ImageGallery images={saturdayImages} title="GlossFest Saturday 24th May" onImageClick={openLightbox} indexOffset={fridayImages.length} />
            <ImageGallery images={sundayImages} title="GlossFest Sunday 25th May" onImageClick={openLightbox} indexOffset={fridayImages.length + saturdayImages.length} />
          </div>
        </div>
      </section>
    </div>
  );
}
